import React from 'react'

import { Box, type LazyToggleMethod } from '@factorialco/deprecated-design-system'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import AddButton from 'modules/shift_management/components/ShiftManagement/AddButton'
import AddShiftSidepanel from 'modules/shift_management/components/ShiftManagement/AddShiftSidepanel'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import { trackAddShiftStartEvent } from 'modules/shift_management/lib/trackEvents'
import { Leave, ShiftsRow } from 'modules/shift_management/lib/types'

interface Props {
  shiftsRow: ShiftsRow
  date: moment.Moment
  leave: Leave
  isBackup: boolean
  isHoveringLeave: boolean
  toggleHovering: LazyToggleMethod
  contractVersions: ContractVersions
}

const AddShiftFromLeaveContextualButton = ({
  shiftsRow,
  date,
  leave,
  isBackup,
  isHoveringLeave,
  toggleHovering,
  contractVersions,
}: Props) => {
  const [openAddShiftSidepanel, setOpenAddShiftSidepanel] = React.useState<boolean>(false)
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()

  const onCloseShiftSidepanel = () => {
    dispatchSelection({
      type: 'CLEAR_SELECTION',
    })
    setOpenAddShiftSidepanel(false)
    toggleHovering(false)()
  }

  const openAddShiftFromLeaveSidepanel = () => {
    const startOn = leave._startOn
    const endOn = leave._finishOn || view.lastDay
    const cellCount = endOn.diff(startOn, 'days') + 1 // +1 to include the first day

    // if view is weekly, handle selection with leave range
    // otherwise with date so that we can add shifts individually for each day
    // since visually we only have range on the weekly view
    if (view.isWeekly) {
      dispatchSelection({
        type: 'SELECT_RANGE',
        startOn,
        endOn,
        shiftsRow,
        view,
      })
    } else {
      dispatchSelection({
        type: 'TOGGLE_DAY',
        day: date,
        shiftsRow,
        view,
        forcedValue: true,
      })
    }
    trackAddShiftStartEvent({
      view,
      impactedEmployees: 1,
      cellCount,
      shiftType: ADD_SHIFT_OPTIONS.savedCustom,
      from: 'contextual_button',
      isBackup,
    })
    setOpenAddShiftSidepanel(true)
  }

  return (
    // Stop propagation to avoid unselecting the cell on the background.
    <Box onClick={(ev) => ev.stopPropagation()}>
      <AddButton
        onClick={openAddShiftFromLeaveSidepanel}
        isVisible={isHoveringLeave && !openAddShiftSidepanel}
      />
      {openAddShiftSidepanel && (
        <AddShiftSidepanel
          onClose={onCloseShiftSidepanel}
          isBackup={isBackup}
          leave={leave}
          contractVersions={contractVersions}
        />
      )}
    </Box>
  )
}

export default AddShiftFromLeaveContextualButton
